import { createSlice } from "@reduxjs/toolkit";

import {
  getAllColumnsAction,
  getIndividualClientColumnAction,
  assignColumnsToClientAction,
  addInvoiceAction,
  getExpensesInvoiceListAction,
  getRevenueInvoiceListAction,
  getOtherEntriesListAction,
  viewInvoiceAction,
  editInvoiceAction,
  deleteInvoiceAction,
  deleteDocumentAction,
  downloadInvoiceAction,
  getDocumentListAction,
  invoiceBulkUploadAction,
} from "@/redux/invoicing/actions";
import { initialState } from "@/redux/invoicing/initialState";
import type { InvoicingTypes } from "@/redux/invoicing/invoicingTypes";
import { invoicingReducer } from "@/redux/invoicing/reducer";

export const invoicingSlice = createSlice({
  name: "invoicing",
  initialState,
  reducers: invoicingReducer,

  extraReducers: (builder) => {
    builder.addCase(getAllColumnsAction.pending, (state: InvoicingTypes) => ({
      ...state,
      fetching: true,
    }));

    builder.addCase(
      getAllColumnsAction.fulfilled,
      (state: InvoicingTypes, { payload }) => {
        const { result = {}, message, status_code, success } = payload;

        if (success === 0) {
          return {
            ...state,
            fetching: false,
            error: message,
            success,
            statusCode: status_code,
          };
        }

        return {
          ...state,
          fetching: false,
          columnList: result,
        };
      }
    );

    builder.addCase(
      getAllColumnsAction.rejected,
      (state: InvoicingTypes, { error }) => ({
        ...state,
        fetching: false,
        error: error?.message ?? null,
      })
    );

    builder.addCase(
      getIndividualClientColumnAction.pending,
      (state: InvoicingTypes) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getIndividualClientColumnAction.fulfilled,
      (state: InvoicingTypes, { payload }) => {
        const { result = {}, success, message, status_code } = payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success,
            error: message,
            statusCode: status_code,
          };
        }

        return {
          ...state,
          fetching: false,
          individualColumnList: result,
        };
      }
    );

    builder.addCase(
      getIndividualClientColumnAction.rejected,
      (state: InvoicingTypes) => ({
        ...state,
        fetching: false,
      })
    );

    builder.addCase(
      assignColumnsToClientAction.pending,
      (state: InvoicingTypes) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      assignColumnsToClientAction.fulfilled,
      (state: InvoicingTypes, { payload }) => {
        const { success, message, status_code } = payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            error: message,
            success,
            statusCode: status_code,
          };
        }

        return {
          ...state,
          fetching: false,
          success,
          statusCode: status_code,
        };
      }
    );

    builder.addCase(
      assignColumnsToClientAction.rejected,
      (state: InvoicingTypes, { error }) => {
        return {
          ...state,
          fetching: false,
          error: error?.message ?? null,
        };
      }
    );

    builder.addCase(invoiceBulkUploadAction.pending, (state: any) => ({
      ...state,
      fetching: true,
    }));

    builder.addCase(
      invoiceBulkUploadAction.fulfilled,
      (state: any, { payload }) => {
        // @ts-ignore [TODO:] Need to remove this ts-ignore by giving proper type
        const { success, message, status_code, data = {} } = payload;

        if (success === 0) {
          return {
            ...state,
            fetching: false,
            error: message,
            statusCode: status_code,
            success,
          };
        }

        return {
          ...state,
          fetching: false,
          statusCode: status_code,
          bulkUploadData: data,
        };
      }
    );

    builder.addCase(
      invoiceBulkUploadAction.rejected,
      (state: any, { error }) => ({
        ...state,
        fetching: false,
        error: error?.message,
      })
    );

    builder.addCase(viewInvoiceAction.pending, (state: InvoicingTypes) => ({
      ...state,
      fetching: true,
    }));

    builder.addCase(
      viewInvoiceAction.fulfilled,
      (state: InvoicingTypes, { payload }) => {
        const { success, message, status_code, result = {} } = payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            error: message,
            statusCode: status_code,
            success,
          };
        }

        return {
          ...state,
          fetching: false,
          statusCode: status_code,
          viewInvoiceData: result,
        };
      }
    );

    builder.addCase(
      viewInvoiceAction.rejected,
      (state: InvoicingTypes, { error }) => ({
        ...state,
        fetching: false,
        error: error?.message ?? null,
      })
    );

    builder.addCase(deleteDocumentAction.pending, (state: any) => ({
      ...state,
      fetching: true,
    }));

    builder.addCase(
      deleteDocumentAction.fulfilled,
      (state: InvoicingTypes, { payload }) => {
        const { success, message, result, status_code } = payload;
        if (success === 0) {
          return {
            ...state,
            error: message,
            statusCode: status_code,
            success,
          };
        }

        return {
          ...state,
          fetching: false,
          statusCode: status_code,
          message,
          success,
        };
      }
    );

    builder.addCase(
      deleteDocumentAction.rejected,
      (state: InvoicingTypes, { error }) => ({
        ...state,
        fetching: false,
        error: error?.message ?? null,
      })
    );

    builder.addCase(deleteInvoiceAction.pending, (state: InvoicingTypes) => ({
      ...state,
      fetching: true,
    }));

    builder.addCase(
      deleteInvoiceAction.fulfilled,
      (state: InvoicingTypes, { payload }) => {
        const { success, message, result, status_code } = payload;
        if (success === 0) {
          return {
            ...state,
            error: message,
            statusCode: status_code,
            fetching: false,
            success,
          };
        }

        return {
          ...state,
          fetching: false,
          statusCode: status_code,
        };
      }
    );

    builder.addCase(
      deleteInvoiceAction.rejected,
      (state: InvoicingTypes, { error }) => ({
        ...state,
        fetching: false,
        error: error?.message ?? null,
      })
    );

    builder.addCase(downloadInvoiceAction.pending, (state: InvoicingTypes) => ({
      ...state,
      fetching: true,
    }));

    builder.addCase(
      downloadInvoiceAction.fulfilled,
      (state: InvoicingTypes, { payload }) => {
        const { success, message, status_code, result = {} } = payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            error: message,
          };
        }
        return {
          ...state,
          fetching: false,
          // result,
          statusCode: status_code,
          success,
        };
      }
    );

    builder.addCase(
      downloadInvoiceAction.rejected,
      (state: InvoicingTypes, { error }) => ({
        ...state,
        fetching: false,
        error: error?.message ?? null,
      })
    );

    builder.addCase(editInvoiceAction.pending, (state: InvoicingTypes) => ({
      ...state,
      fetching: true,
    }));

    builder.addCase(
      editInvoiceAction.fulfilled,
      (state: InvoicingTypes, { payload }) => {
        const { success, message, status_code, result = {} } = payload;

        if (success === 0) {
          return {
            ...state,
            fetching: false,
            error: message,
            statusCode: status_code,
            success,
          };
        }

        return {
          ...state,
          fetching: false,
          statusCode: status_code,
          success,
        };
      }
    );

    builder.addCase(
      editInvoiceAction.rejected,
      (state: InvoicingTypes, { error }) => ({
        ...state,
        fetching: false,
        error: error?.message ?? null,
      })
    );

    builder.addCase(getDocumentListAction.pending, (state: any) => ({
      ...state,
      fetching: true,
    }));

    builder.addCase(
      getDocumentListAction.fulfilled,
      (state: any, { payload }) => {
        const { success, message, status_code, result = {} } = payload;

        if (success === 0) {
          return {
            ...state,
            fetching: false,
            error: message,
            statusCode: status_code,
            success,
          };
        }

        return {
          ...state,
          fetching: false,
          statusCode: status_code,
          documentList: result,
        };
      }
    );

    builder.addCase(
      getDocumentListAction.rejected,
      (state: any, { error }) => ({
        ...state,
        fetching: false,
        error: error?.message,
      })
    );

    builder.addCase(addInvoiceAction.pending, (state: InvoicingTypes) => ({
      ...state,
      fetching: true,
    }));

    builder.addCase(
      addInvoiceAction.fulfilled,
      (state: InvoicingTypes, { payload }) => {
        const { success, message, status_code, result = {} } = payload;

        if (success === 0) {
          return {
            ...state,
            fetching: false,
            error: message,
            statusCode: status_code,
            success,
          };
        }

        return {
          ...state,
          fetching: false,
          statusCode: status_code,
          success,
        };
      }
    );

    builder.addCase(
      addInvoiceAction.rejected,
      (state: InvoicingTypes, { error }) => ({
        ...state,
        fetching: false,
        error: error?.message ?? null,
      })
    );

    builder.addCase(
      getExpensesInvoiceListAction.pending,
      (state: InvoicingTypes) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getExpensesInvoiceListAction.fulfilled,
      (state: InvoicingTypes, { payload }) => {
        const { success, message, result, status_code } = payload;
        let count = 0;
        if (result?.data) {
          result?.data?.map((val: any) => {
            if (val?.upload === null) {
              count++;
            }
          });
        }

        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success,
            error: message,
            statusCode: status_code,
          };
        }

        return {
          ...state,
          fetching: false,
          expensesInvoiceList: result,
          missingExpensesAttachmentCount: count,
        };
      }
    );

    builder.addCase(
      getExpensesInvoiceListAction.rejected,
      (state: InvoicingTypes, { error }) => ({
        ...state,
        fetching: false,
        error: error?.message ?? null,
      })
    );

    builder.addCase(
      getRevenueInvoiceListAction.pending,
      (state: InvoicingTypes) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getRevenueInvoiceListAction.fulfilled,
      (state: InvoicingTypes, { payload }) => {
        const { success, message, result, status_code } = payload;
        let count = 0;
        if (result?.data) {
          result?.data?.map((val: any) => {
            if (val?.upload === null) {
              count++;
            }
          });
        }

        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success,
            error: message,
            statusCode: status_code,
          };
        }

        return {
          ...state,
          fetching: false,
          revenueInvoiceList: result,
          missingRevenueAttachmentCount: count,
        };
      }
    );

    builder.addCase(
      getOtherEntriesListAction.rejected,
      (state: InvoicingTypes, { error }) => ({
        ...state,
        fetching: false,
        error: error?.message ?? null,
      })
    );

    builder.addCase(
      getOtherEntriesListAction.pending,
      (state: InvoicingTypes) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getOtherEntriesListAction.fulfilled,
      (state: InvoicingTypes, { payload }) => {
        const { success, message, result, status_code } = payload;
        let count = 0;
        if (result?.data) {
          result?.data?.map((val: any) => {
            if (val?.upload === null) {
              count++;
            }
          });
        }

        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success,
            error: message,
            statusCode: status_code,
          };
        }

        return {
          ...state,
          fetching: false,
          otherEntriesList: result,
          missingOthersAttachmentCount: count,
        };
      }
    );
  },
});

export const {
  resetError: resetInvoicingError,
  resetSuccess: resetInvoicingSuccess,
  resetStatusCode: resetInvoiceStatusCode,
  resetInvoicingFetching,
  resetBulkUploadData,
} = invoicingSlice.actions;
